import React, { useState, useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import aspectRatios from '../../constants/aspectRatios';
import resolutionMap from '../../constants/resolutionMap';
import { generateCADBasedMoodRoom, getErrorMessage } from '../../services/geminiService';
import { generateWithFlux, isFluxModel } from '../../services/fluxService';
import { useProjects } from '../../contexts/ProjectContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  projectsService,
  savedDesignsService,
  generationMetadataService,
  userProfilesService,
  storageService,
} from '../../services/supabaseService';
import { supabase } from '../../lib/supabase';
import toast, { Toaster } from 'react-hot-toast';
import { getModelCreditCost } from '../../utils/creditCost';
import { checkSaveDesignLimit } from '../../utils/planLimits';
import { downloadImage } from '../../utils/downloadUtils';

import CADImageUpload from './components/CADImageUpload';
import MoodImageUpload from './components/MoodImageUpload';
import CADSettings from './components/CADSettings';
import ResultDisplay from './components/ResultDisplay';
import GenerationPanel from './components/GenerationPanel';
import AssetLibraryPanel from '../../components/AssetLibraryPanel';
import GenerationStatusNotification from '../../components/GenerationStatusNotification';
import useGenerationTimeout from '../../hooks/useGenerationTimeout';

const CADBasedMoodRoomGenerator = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showSaveModal } = useProjects();
  const { user, userProfile, loading, profileLoading } = useAuth();
  const [currentProjectId, setCurrentProjectId] = useState(null);

  // Virtual Tool Guide state
  const [showGuide, setShowGuide] = useState(false);
  const [guideStep, setGuideStep] = useState(1);
  const [guideAnimating, setGuideAnimating] = useState(false);
  const [guideVisible, setGuideVisible] = useState(false);
  const guideBubblePosRef = useRef({ top: '50%', left: '50%' });

  const cadUploadDesktopRef = useRef(null);
  const cadUploadMobileRef = useRef(null);
  const moodUploadDesktopRef = useRef(null);
  const moodUploadMobileRef = useRef(null);
  const assetLibraryDesktopRef = useRef(null);
  const assetLibraryMobileRef = useRef(null);
  const spacePlannerDesktopRef = useRef(null);
  const spacePlannerMobileRef = useRef(null);
  const commonAngleDesktopRef = useRef(null);
  const commonAngleMobileRef = useRef(null);
  const angleViewDesktopRef = useRef(null);
  const angleViewMobileRef = useRef(null);
  const roomTypeDesktopRef = useRef(null);
  const roomTypeMobileRef = useRef(null);
  const modelSelectionDesktopRef = useRef(null);
  const modelSelectionMobileRef = useRef(null);
  const aspectRatioDesktopRef = useRef(null);
  const aspectRatioMobileRef = useRef(null);
  const resolutionDesktopRef = useRef(null);
  const resolutionMobileRef = useRef(null);
  const promptDesktopRef = useRef(null);
  const promptMobileRef = useRef(null);
  const generateDesktopRef = useRef(null);
  const generateMobileRef = useRef(null);
  const resultDesktopRef = useRef(null);
  const resultMobileRef = useRef(null);

  const scrollGuideTarget = (step) => {
    const desktopRefs = [cadUploadDesktopRef, moodUploadDesktopRef, assetLibraryDesktopRef, spacePlannerDesktopRef, commonAngleDesktopRef, angleViewDesktopRef, roomTypeDesktopRef, modelSelectionDesktopRef, aspectRatioDesktopRef, resolutionDesktopRef, promptDesktopRef, generateDesktopRef, resultDesktopRef];
    const mobileRefs = [cadUploadMobileRef, moodUploadMobileRef, assetLibraryMobileRef, spacePlannerMobileRef, commonAngleMobileRef, angleViewMobileRef, roomTypeMobileRef, modelSelectionMobileRef, aspectRatioMobileRef, resolutionMobileRef, promptMobileRef, generateMobileRef, resultMobileRef];
    const ref = window.innerWidth >= 768 ? desktopRefs[step - 1]?.current : mobileRefs[step - 1]?.current;
    if (ref) {
      ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const openGuide = () => {
    setGuideStep(1);
    setShowGuide(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setGuideVisible(true));
    });
  };

  const nextGuideStep = () => {
    setGuideAnimating(true);
    setGuideVisible(false);
    const nextStep = guideStep + 1;
    const needsScroll = nextStep === 4 || nextStep === 5 || nextStep === 10 || nextStep === 11 || nextStep === 12 || nextStep === 13;
    if (needsScroll) {
      scrollGuideTarget(nextStep);
    }
    setTimeout(() => {
      setGuideStep(nextStep);
      setGuideAnimating(false);
      if (needsScroll) {
        setTimeout(() => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => setGuideVisible(true));
          });
        }, 400);
      } else {
        setTimeout(() => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => setGuideVisible(true));
          });
        }, 100);
      }
    }, 700);
  };

  const closeGuide = () => {
    setGuideVisible(false);
    setTimeout(() => {
      setShowGuide(false);
      setGuideStep(1);
    }, 600);
  };

  // Core state for CAD tool
  const [cadImages, setCadImages] = useState([]);
  const [moodImages, setMoodImages] = useState([]);
  const [selectedModel, setSelectedModel] = useState('Gemini 3.1 Flash Image Preview');
  const [selectedImageSize, setSelectedImageSize] = useState('Auto');
  const [isAspectRatioOpen, setIsAspectRatioOpen] = useState(false);
  const [selectedResolution, setSelectedResolution] = useState('1K');
  const [isResolutionOpen, setIsResolutionOpen] = useState(false);
  const [promptLeft, setPromptLeft] = useState('');
  const [selectedRoomTypeLeft, setSelectedRoomTypeLeft] = useState('--');
  const [commonAngleViewLeft, setCommonAngleViewLeft] = useState('frontal');
  const [selectedAngleViewLeft, setSelectedAngleViewLeft] = useState('--');

  // Generation state
  const [currentImage, setCurrentImage] = useState(null);
  const [sessionLibraryImages, setSessionLibraryImages] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState('');

  const [libraryAssets, setLibraryAssets] = useState([]);
  const [currentDesignId, setCurrentDesignId] = useState(null);

  // Drag and drop states
  const [isDragOverCAD, setIsDragOverCAD] = useState(false);
  const [isDragOverMood, setIsDragOverMood] = useState(false);

  const { generationStatus, startTimer, clearTimer, setFailed, setCancelled, dismissNotification } = useGenerationTimeout();

  // File input refs
  const abortControllerRef = useRef(null);
  const isGeneratingRef = useRef(false);

  const MAX_PROMPT_LENGTH = 1600;
  const MAX_TOTAL_UPLOADS = 6;
  const totalUploads = cadImages.length + moodImages.length;
  const maxRemaining = Math.max(0, MAX_TOTAL_UPLOADS - totalUploads);


  // Restore project data when opened from /project-history
  useEffect(() => {
    const projectId = location.state?.projectId;
    if (!projectId) return;
    const loadProjectData = async () => {
      setCurrentProjectId(projectId);
      const designs = await savedDesignsService.getDesigns(projectId);
      if (designs && designs.length > 0) {
        const imageUrls = designs.map((d) => d.imageUrl).filter(Boolean);
        if (imageUrls.length > 0) setSessionLibraryImages(imageUrls);
        setCurrentImage(designs[0].imageUrl);
        setCurrentDesignId(designs[0].id);
        // Restore generation settings from metadata
        const metadata = await generationMetadataService.getMetadata(designs[0].id);
        if (metadata) {
          if (metadata.modelUsed) setSelectedModel(metadata.modelUsed);
          if (metadata.imageSize) setSelectedImageSize(metadata.imageSize);
          if (metadata.roomType && metadata.roomType !== 'Room') setSelectedRoomTypeLeft(metadata.roomType);
          if (metadata.angleView && metadata.angleView !== '--') setSelectedAngleViewLeft(metadata.angleView);
          if (metadata?.usedMedia?.length > 0) setLibraryAssets(metadata.usedMedia);
          if (metadata.additionalParams?.prompt) setPromptLeft(metadata.additionalParams.prompt);
          if (metadata.additionalParams?.commonAngleViewLeft) setCommonAngleViewLeft(metadata.additionalParams.commonAngleViewLeft);
        }
      }
    };
    loadProjectData();
  }, []);

  // Get image dimensions based on selected aspect ratio and resolution
  const getImageDimensions = () => {
    const ratioResolutions = resolutionMap[selectedImageSize];
    if (ratioResolutions && ratioResolutions[selectedResolution]) {
      return ratioResolutions[selectedResolution];
    }
    // Fallback to aspectRatios constant
    const selectedRatio = aspectRatios.find(ratio => ratio.value === selectedImageSize);
    return selectedRatio ? { width: selectedRatio.width, height: selectedRatio.height } : { width: 1024, height: 1024 };
  };

  // Handle generation
  const handleGenerate = async () => {
    if (isGeneratingRef.current) return;
    isGeneratingRef.current = true;

    if (cadImages.length === 0) {
      toast.error('Please upload at least one CAD technical drawing image.');
      isGeneratingRef.current = false;
      return;
    }

    if (moodImages.length === 0) {
      toast.error('Please upload at least one mood reference image.');
      isGeneratingRef.current = false;
      return;
    }

    // Auth check
    const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();
    if (!session || !session?.user?.id) {
      toast.error('Please sign in to generate images.');
      isGeneratingRef.current = false;
      return;
    }
    const currentUser = session?.user;

    // Credit check (supports team members sharing owner's pool)
    const creditCost = getModelCreditCost(selectedModel);
    const creditCheck = await userProfilesService?.checkCredits(currentUser?.id, creditCost);
    if (!creditCheck?.allowed) {
      toast.error(creditCheck?.message || 'Insufficient credits.');
      isGeneratingRef.current = false;
      return;
    }

    // Check storage limit
    {
      const { allowed, current, max } = await checkSaveDesignLimit(currentUser?.id, userProfile?.subscription_tier);
      if (!allowed) {
        toast.error(`Storage limit reached (${current}/${max}). Please upgrade your plan or delete existing files.`);
        isGeneratingRef.current = false;
        return;
      }
    }

    setIsGenerating(true);
    startTimer();
    setGenerationError('');
    setCurrentImage(null);

    // Create abort controller
    abortControllerRef.current = new AbortController();

    try {
      const dimensions = getImageDimensions();

      // Build CAD-specific system prompt exactly as specified
      const cadSystemPrompt = `Role: You are a neutral translator between interior design platform user inputs and Gemini 3.1 Flash Image Preview image generation model for CAD-to-rendered-room visualization.
Send all image references and text inputs uploaded by the user directly to Gemini API in one single call.
Intro: /cad-mood-room-generator is intended to convert CAD technical drawings (floor plans, top-down layouts, architectural plans — uploaded as PNG/JPG images, not real AutoCAD files) into hyper-realistic furnished 3D room renders. The user uploads a CAD technical drawing image and mood reference image(s). The CAD drawing defines the exact spatial layout, room proportions, wall positions, and furniture placement zones. The mood images define the color palette, material finishes, furniture style, and atmosphere. The CAD layout is furnished and rendered into a hyper-realistic 3D room image that follows the CAD structure precisely while applying the visual identity from the mood references.
Task: Preserve the CAD spatial structure precisely BUT render it as a completely photorealistic interior scene. Apply mood and atmosphere from mood reference images only. Apply furnished interiors with hyper realism and photorealism quality. Do not invent unrequested elements. Maintain a high consistency to CAD layout and mood references. Preserve the logic and spatial adequacy of generated rooms according to the normal physical human world, with reasonable room dimensions. The room composition must be logically and aesthetically appealing and coherent. CRITICAL: The final output must look like a professional interior photograph - no technical drawing elements should be visible.
High-quality keywords to maintain in every generation: render the CAD layout in the user-provided mood, hyper realism, photorealism, 3Ds Max quality with textures and realistic surfaces, shot on professional camera
Temperature: 1 (default Gemini).

You will receive these inputs from the user, and you need to send them to Gemini API in one call, in the following importance hierarchy from one to seven:
cadImages / zoneUploadImages (REQUIRED) — CAD technical drawing image uploaded by the user. This defines the exact spatial layout, wall positions, room proportions, door/window placements, and furniture placement zones. CRITICAL: Extract ONLY the spatial information from this CAD drawing - do NOT render the technical drawing elements (black outlines, dimension lines, symbols, text, or any line work). Use the CAD drawing purely as an invisible blueprint to understand room layout, wall positions, and furniture placement zones. The final image must be a photorealistic interior with NO visible CAD elements, lines, or technical drawing artifacts.
moodImages (REQUIRED) — atmosphere and style reference images. Extract ONLY the color palette, material colors and textures, furniture style, and overall visual mood visible in these images. Apply it to the CAD structure only.
promptLeft — include the user's text literally, word for word${promptLeft ? ': ' + promptLeft : '.'}
selectedRoomTypeLeft — CRITICAL ROOM FOCUS REQUIREMENT: ${(selectedRoomTypeLeft === '--' || selectedRoomTypeLeft === 'Custom') ? 'No specific room type selected. Render the full CAD layout without focusing on any specific room.' : `The user has selected "${selectedRoomTypeLeft}" as the target room type. You MUST focus exclusively on the ${selectedRoomTypeLeft} area within the CAD drawing. If the CAD shows a full house plan, identify and render ONLY the ${selectedRoomTypeLeft} space. Ignore all other rooms and areas. Frame the output to show specifically the ${selectedRoomTypeLeft} interior with its walls, layout, and designated furniture placement zones. Do not include hallways, other rooms, or exterior areas unless they are directly connected and essential to the ${selectedRoomTypeLeft} context.`}
commonAngleViewLeft — if 'frontal', render the room as a frontal camera perspective view. If 'topdown', render as a top-down overhead view preserving the CAD plan layout but furnished and colored${commonAngleViewLeft === 'frontal' ? ': frontal view' : commonAngleViewLeft === 'topdown' ? ': top-down view' : '.'}
selectedAngleViewLeft — additional angle specification, include only if not '--'${selectedAngleViewLeft && selectedAngleViewLeft !== '--' ? ': ' + selectedAngleViewLeft : '.'}
selectedImageSizeLeft — extract ratio strictly from what the user selected in the UI. If Auto, pass the aspect ratio of cadImages. If a specific size is selected, pass that value exactly. Do not hardcode any dimensions: ${selectedImageSize}.

STRICT RULES:
Do not invent materials, furniture, lighting, or atmosphere not present in the inputs.
Do not use any style labels unless the user wrote them in promptLeft. Strictly follow user's reference images and prompt, and the following task you're trained on related to CAD hyper-realistic rendering.
CAD structure is absolute — never alter spatial layout, wall positions, room proportions, or door/window placements from the CAD drawing.
Mood images define atmosphere only — never override CAD structure.
CAD INVISIBILITY RULE: The CAD drawing is a blueprint only. DO NOT render any visible CAD elements including: black lines, white lines, technical symbols, dimension markings, text labels, floor plan outlines, architectural symbols, or any line work. The CAD provides spatial information only - walls, furniture positions, room boundaries - but these technical drawing elements must be completely invisible in the final photorealistic render.
ROOM FOCUS RULE: ${(selectedRoomTypeLeft === '--' || selectedRoomTypeLeft === 'Custom') ? 'No specific room type selected — render the full CAD layout.' : `If the CAD shows multiple rooms or a full house plan, you MUST identify and focus ONLY on the ${selectedRoomTypeLeft} area. Crop or frame the view to show exclusively the ${selectedRoomTypeLeft} space. Do not render the entire house - focus specifically on the selected room type.`}
If any input is missing or '--', skip it silently without generating filler content.
No explanations, no formatting, no headings in output.
modelSelection — use only to determine which image generation API to call. Do not pass the model name as text in the prompt.`;

      const baseInstruction = "Generate exactly what the prompt describes. Do not add style, materials, or aesthetic elements not mentioned in the prompt. Render with hyper realism and photorealism quality.";
      const fullPrompt = `${baseInstruction}\n\n${cadSystemPrompt}`;

      let generatedImage;

      if (isFluxModel(selectedModel)) {
        generatedImage = await generateWithFlux({
          modelName: selectedModel,
          directPromptText: fullPrompt,
          userPromptText: promptLeft,
          moodboardImages: moodImages,
          referenceImages: cadImages,
          roomType: selectedRoomTypeLeft === 'Custom' ? '' : selectedRoomTypeLeft,
          selectedAngleShot: selectedAngleViewLeft || commonAngleViewLeft,
          imageSize: selectedImageSize,
          signal: abortControllerRef.current.signal,
        });
      } else {
        generatedImage = await generateCADBasedMoodRoom({
          cadImages,
          moodImages,
          userPromptText: promptLeft,
          roomType: selectedRoomTypeLeft === 'Custom' ? '' : selectedRoomTypeLeft,
          commonAngleView: commonAngleViewLeft,
          selectedAngleView: selectedAngleViewLeft,
          imageSize: selectedImageSize,
          modelName: selectedModel,
          dimensions,
          toolSystemPrompt: fullPrompt,
          signal: abortControllerRef.current.signal,
        });
      }

      // Only deduct credits after successful generation
      clearTimer();
      setCurrentImage(generatedImage);
      setLibraryAssets((prev) => [...prev, generatedImage]);
      setSessionLibraryImages((prev) => [...prev, generatedImage]);
      // Save to database
      await saveGenerationToDatabase(generatedImage, promptLeft || '', currentUser);
      // Deduct credits
      await userProfilesService?.updateCredits(currentUser?.id, creditCost);
      toast.success('CAD room render generated successfully!');
      showSaveModal(generatedImage, location.pathname);

    } catch (error) {
      if (error.name === 'AbortError' || error.message.includes('cancelled')) {
        // User cancelled - no charge
        setCancelled();
      } else {
        setFailed();
        setGenerationError(getErrorMessage(error));
      }
    } finally {
      setIsGenerating(false);
      abortControllerRef.current = null;
      isGeneratingRef.current = false;
    }
  };

  // Save generation to database
  const saveGenerationToDatabase = async (imageUrl, prompt, currentUser = null) => {
    const activeUser = currentUser || user;
    if (!activeUser) return;

    try {
      // Upload image to Supabase Storage (convert base64 → public URL)
      const storageUrl = await storageService.uploadImage(imageUrl, activeUser?.id);
      imageUrl = storageUrl;

      let projectId = currentProjectId;
      if (!projectId) {
        const projectName = `${selectedRoomTypeLeft || 'CAD Room'} - ${new Date()?.toLocaleDateString()}`;
        const { data: project, error: projectError } = await projectsService?.createProject(activeUser?.id, {
          name: projectName,
          description: prompt,
          thumbnail: imageUrl,
          toolType: 'cad-mood-room',
          toolName: 'CAD-Based Mood Room Generator',
          status: 'completed',
          tags: [selectedRoomTypeLeft, selectedModel]?.filter(Boolean),
          variations: 1,
        });
        if (projectError) { console.error('Error creating project:', projectError); return; }
        projectId = project?.id;
        setCurrentProjectId(projectId);
      } else {
        await projectsService?.incrementVariations(projectId);
        await projectsService?.updateProject(projectId, { thumbnail: imageUrl });
      }

      const { data: design, error: designError } = await savedDesignsService?.createDesign(activeUser?.id, projectId, {
        imageUrl,
        prompt,
        isFavorite: false,
      });
      if (designError) { console.error('Error saving design:', designError); return; }

      await generationMetadataService?.createMetadata(activeUser?.id, design?.id, {
        modelUsed: selectedModel,
        imageSize: selectedImageSize,
        roomType: (selectedRoomTypeLeft === 'Custom' ? '' : selectedRoomTypeLeft) || 'Room',
        angleView: selectedAngleViewLeft || '--',
        style: 'CAD-Based Mood Room',
        resolution: selectedImageSize,
        cameraView: selectedAngleViewLeft || '--',
        lighting: 'Auto',
        quality: 'High',
        usedMedia: [...cadImages, ...moodImages, imageUrl],
        additionalParams: {
          prompt: promptLeft || '',
          commonAngleViewLeft: commonAngleViewLeft || 'frontal',
        },
      });
    } catch (error) {
      console.error('Error saving to database:', error);
    }
  };

  // Handle cancel generation
  const handleCancelGeneration = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setCancelled();
    setIsGenerating(false);
  };

  // Handle prompt changes with character limit
  const handlePromptChange = (e) => {
    const value = e.target.value;
    if (value.length <= MAX_PROMPT_LENGTH) {
      setPromptLeft(value);
    }
  };

  // Download image
  const handleDownload = async () => {
    if (!currentImage) return;
    await downloadImage(currentImage, `cad-mood-room-${Date.now()}.png`);
  };

  return (
    <>
      <GenerationStatusNotification status={generationStatus} onDismiss={dismissNotification} />
      <Helmet>
        <title>CAD Based Mood Room Generator - Roomwork.ai</title>
        <meta
          name="description"
          content="Generate stunning interior designs using CAD-based mood room technology with AI-powered design generation"
        />
      </Helmet>
      <div className="min-h-screen bg-black overflow-x-hidden">
        <Header />
        <main className="pt-20 lg:pt-24 px-4 pb-[20vh]">
          <div className="max-w-[1400px] mx-auto">
            <div className="hidden md:flex gap-4 justify-center mb-6">
              <div className="w-[400px] flex-shrink-0">
                <button
                  onClick={() => navigate('/main-dashboard')}
                  className="flex items-center space-x-2 text-white/70 hover:text-white transition-colors mb-4">
                  <Icon name="ArrowLeft" size={20} />
                  <span className="text-sm font-medium">Back to Dashboard</span>
                </button>
                <h1 className="text-3xl font-bold text-white">
                  CAD Based Mood Room Generator
                </h1>
              </div>
              <div className="flex-1 max-w-[900px] flex items-end justify-end gap-3 flex-shrink min-w-0">
                <button
                  onClick={() => openGuide()}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-medium hover:scale-[1.02] transition-all flex-shrink-0"
                  style={{ background: 'rgba(255, 255, 255, 0.22)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', border: '1px solid rgba(255, 255, 255, 0.4)', boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.25)' }}
                >
                  <Icon name="HelpCircle" size={18} />
                  <span className="hidden lg:inline">Virtual Tool Guide</span>
                </button>
                <button
                  onClick={() => navigate('/project-history')}
                  className="flex items-center gap-2 px-4 py-2 glassmorphic-button rounded-xl text-white text-sm hover:scale-105 transition-transform flex-shrink-0">
                  <Icon name="History" size={18} />
                  <span className="hidden lg:inline">View Projects</span>
                </button>
              </div>
            </div>

            {/* Mobile Header */}
            <div className="md:hidden mb-6">
              <button
                onClick={() => navigate('/main-dashboard')}
                className="flex items-center space-x-2 text-white/70 hover:text-white transition-colors mb-4">
                <Icon name="ArrowLeft" size={20} />
                <span className="text-sm font-medium">Back to Dashboard</span>
              </button>
              <div className="flex items-start justify-between gap-3">
                <h1 className="text-2xl font-bold text-white">
                  CAD Based Mood<br />Room Generator
                </h1>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openGuide()}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-white text-xs font-medium hover:scale-[1.02] transition-all whitespace-nowrap flex-shrink-0"
                    style={{ background: 'rgba(255, 255, 255, 0.22)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', border: '1px solid rgba(255, 255, 255, 0.4)', boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.25)' }}
                  >
                    <Icon name="HelpCircle" size={16} />
                    Guide
                  </button>
                  <button
                    onClick={() => navigate('/project-history')}
                    className="flex items-center gap-2 px-4 py-2 glassmorphic-button rounded-xl text-white text-sm hover:scale-105 transition-transform flex-shrink-0 mt-1">
                    <Icon name="History" size={18} />
                    <span className="hidden md:inline">View Projects</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Desktop/Tablet/Laptop Layout (2-column) */}
            <div className="hidden md:flex gap-4 justify-center">
              {/* Left Panel - Upload Windows Stacked Vertically */}
              <div className="w-[400px] flex-shrink-0 space-y-4">
                {/* Upload counter */}
                <div className="text-left">
                  <span className={`text-sm font-medium ${totalUploads >= MAX_TOTAL_UPLOADS ? "text-red-400" : "text-white/70"}`}>
                    {totalUploads} / {MAX_TOTAL_UPLOADS} images uploaded
                  </span>
                </div>

                {/* CAD Images Upload */}
                <div
                  ref={cadUploadDesktopRef}
                  className="border border-transparent transition-all duration-200 rounded-2xl"
                  style={{ position: showGuide && guideStep === 1 ? 'relative' : undefined, zIndex: showGuide && guideStep === 1 ? 201 : undefined, boxShadow: showGuide && guideStep === 1 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)', borderRadius: '1rem' }}
                  onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; e.currentTarget.classList.add('border-orange-500/50'); }}
                  onDragLeave={(e) => { e.currentTarget.classList.remove('border-orange-500/50'); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('border-orange-500/50');
                    const imageUrl = e.dataTransfer.getData('imageUrl') || e.dataTransfer.getData('text/plain');
                    if (imageUrl && totalUploads < MAX_TOTAL_UPLOADS) {
                      setCadImages((prev) => prev.includes(imageUrl) ? prev : [...prev, imageUrl]);
                    }
                  }}
                >
                  <CADImageUpload
                    cadImages={cadImages}
                    setCadImages={setCadImages}
                    isDragOver={isDragOverCAD}
                    setIsDragOver={setIsDragOverCAD}
                    maxRemaining={maxRemaining}
                    userId={user?.id}
                    storageService={storageService}
                  />
                </div>

                {/* Mood Images Upload */}
                <div
                  ref={moodUploadDesktopRef}
                  className="border border-transparent transition-all duration-200 rounded-2xl"
                  style={{ position: showGuide && guideStep === 2 ? 'relative' : undefined, zIndex: showGuide && guideStep === 2 ? 201 : undefined, boxShadow: showGuide && guideStep === 2 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)', borderRadius: '1rem' }}
                  onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; e.currentTarget.classList.add('border-orange-500/50'); }}
                  onDragLeave={(e) => { e.currentTarget.classList.remove('border-orange-500/50'); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('border-orange-500/50');
                    const imageUrl = e.dataTransfer.getData('imageUrl') || e.dataTransfer.getData('text/plain');
                    if (imageUrl && totalUploads < MAX_TOTAL_UPLOADS) {
                      setMoodImages((prev) => prev.includes(imageUrl) ? prev : [...prev, imageUrl]);
                    }
                  }}
                >
                  <MoodImageUpload
                    moodImages={moodImages}
                    setMoodImages={setMoodImages}
                    isDragOver={isDragOverMood}
                    setIsDragOver={setIsDragOverMood}
                    maxRemaining={maxRemaining}
                    userId={user?.id}
                    storageService={storageService}
                  />
                </div>

                {/* Asset Library Panel */}
                <div ref={assetLibraryDesktopRef} className="rounded-3xl" style={{ position: showGuide && guideStep === 3 ? 'relative' : undefined, zIndex: showGuide && guideStep === 3 ? 201 : undefined, boxShadow: showGuide && guideStep === 3 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)', borderRadius: '1.5rem' }}>
                  <AssetLibraryPanel
                    libraryAssets={libraryAssets}
                    setLibraryAssets={setLibraryAssets}
                    generatedImages={sessionLibraryImages}
                    setGeneratedImages={setSessionLibraryImages}
                    currentProjectId={currentProjectId}
                    onImageDeleted={(remaining) => {
                      if (!isGenerating) setCurrentImage(remaining[0] || null);
                    }}
                  />
                </div>

                {/* Link to Space Planner */}
                <div ref={spacePlannerDesktopRef} className="rounded-xl" style={{ position: showGuide && guideStep === 4 ? 'relative' : undefined, zIndex: showGuide && guideStep === 4 ? 201 : undefined, boxShadow: showGuide && guideStep === 4 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)', borderRadius: '0.75rem' }}>
                  <button
                    onClick={() => navigate('/cad-space-planner')}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 hover:border-orange-500/40 hover:bg-orange-500/5 transition-all duration-300 group"
                  >
                    <Icon name="ArrowRight" size={14} className="text-orange-500 group-hover:translate-x-0.5 transition-transform duration-200" />
                    <span className="text-xs text-white/70 group-hover:text-white/90 transition-colors">Continue editing CAD zones separately</span>
                  </button>
                </div>
              </div>

              {/* Center Panel - Main Generation Area */}
              <div className="flex-1 max-w-[900px] space-y-4">
                {/* Result Image Display */}
                <div ref={resultDesktopRef} className="rounded-3xl" style={{ position: showGuide && guideStep === 13 ? 'relative' : undefined, zIndex: showGuide && guideStep === 13 ? 201 : undefined, boxShadow: showGuide && guideStep === 13 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)', borderRadius: '1.5rem' }}>
                <ResultDisplay
                  currentImage={currentImage}
                  setCurrentImage={setCurrentImage}
                  isGenerating={isGenerating}
                  generationError={generationError}
                  setGenerationError={setGenerationError}
                  onDownload={handleDownload}
                  onRegenerate={handleGenerate}
                />
                </div>

                {/* Settings */}
                <CADSettings
                  selectedRoomType={selectedRoomTypeLeft}
                  setSelectedRoomType={setSelectedRoomTypeLeft}
                  commonAngleView={commonAngleViewLeft}
                  setCommonAngleView={setCommonAngleViewLeft}
                  commonAngleRef={commonAngleDesktopRef}
                  commonAngleGlowStyle={{ position: showGuide && guideStep === 5 ? 'relative' : undefined, zIndex: showGuide && guideStep === 5 ? 201 : undefined, boxShadow: showGuide && guideStep === 5 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)', borderRadius: '0.75rem' }}
                  angleViewRef={angleViewDesktopRef}
                  angleViewGlowStyle={{ position: showGuide && guideStep === 6 ? 'relative' : undefined, zIndex: showGuide && guideStep === 6 ? 201 : undefined, boxShadow: showGuide && guideStep === 6 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)', borderRadius: '0.75rem' }}
                  roomTypeRef={roomTypeDesktopRef}
                  roomTypeGlowStyle={{ position: showGuide && guideStep === 7 ? 'relative' : undefined, zIndex: showGuide && guideStep === 7 ? 201 : undefined, boxShadow: showGuide && guideStep === 7 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)', borderRadius: '0.75rem' }}
                  modelSelectionRef={modelSelectionDesktopRef}
                  modelSelectionGlowStyle={{ position: showGuide && guideStep === 8 ? 'relative' : undefined, zIndex: showGuide && guideStep === 8 ? 201 : undefined, boxShadow: showGuide && guideStep === 8 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)', borderRadius: '0.75rem' }}
                  aspectRatioGlowRef={aspectRatioDesktopRef}
                  aspectRatioGlowStyle={{ position: showGuide && guideStep === 9 ? 'relative' : undefined, zIndex: showGuide && guideStep === 9 ? 201 : undefined, boxShadow: showGuide && guideStep === 9 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)', borderRadius: '0.75rem' }}
                  resolutionGlowRef={resolutionDesktopRef}
                  resolutionGlowStyle={{ position: showGuide && guideStep === 10 ? 'relative' : undefined, zIndex: showGuide && guideStep === 10 ? 201 : undefined, boxShadow: showGuide && guideStep === 10 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)', borderRadius: '0.75rem' }}
                  promptGlowRef={promptDesktopRef}
                  promptGlowStyle={{ position: showGuide && guideStep === 11 ? 'relative' : undefined, zIndex: showGuide && guideStep === 11 ? 201 : undefined, boxShadow: showGuide && guideStep === 11 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)', borderRadius: '0.75rem' }}
                  selectedAngleView={selectedAngleViewLeft}
                  setSelectedAngleView={setSelectedAngleViewLeft}
                  selectedModel={selectedModel}
                  setSelectedModel={setSelectedModel}
                  selectedImageSize={selectedImageSize}
                  setSelectedImageSize={setSelectedImageSize}
                  isAspectRatioOpen={isAspectRatioOpen}
                  setIsAspectRatioOpen={setIsAspectRatioOpen}
                  selectedResolution={selectedResolution}
                  setSelectedResolution={setSelectedResolution}
                  isResolutionOpen={isResolutionOpen}
                  setIsResolutionOpen={setIsResolutionOpen}
                  prompt={promptLeft}
                  setPrompt={setPromptLeft}
                  onPromptChange={handlePromptChange}
                />

                {/* Generation Panel */}
                <div ref={generateDesktopRef} className="space-y-4 rounded-2xl" style={{ position: showGuide && guideStep === 12 ? 'relative' : undefined, zIndex: showGuide && guideStep === 12 ? 201 : undefined, boxShadow: showGuide && guideStep === 12 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)', borderRadius: '1rem' }}>
                  <GenerationPanel
                    isGenerating={isGenerating}
                    cadImages={cadImages}
                    moodImages={moodImages}
                    generationError={generationError}
                    onGenerate={handleGenerate}
                    onCancelGeneration={handleCancelGeneration}
                    creditCost={getModelCreditCost(selectedModel)}
                  />
                </div>
              </div>
            </div>

            {/* Mobile Layout (single column) - hidden on desktop */}
            <div className="md:hidden space-y-4">
              {/* Result Image Display */}
              <div ref={resultMobileRef} className="rounded-3xl" style={{ position: showGuide && guideStep === 13 ? 'relative' : undefined, zIndex: showGuide && guideStep === 13 ? 201 : undefined, boxShadow: showGuide && guideStep === 13 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)', borderRadius: '1.5rem' }}>
              <ResultDisplay
                currentImage={currentImage}
                setCurrentImage={setCurrentImage}
                isGenerating={isGenerating}
                generationError={generationError}
                setGenerationError={setGenerationError}
                onDownload={handleDownload}
                onRegenerate={handleGenerate}
              />
              </div>

              {/* Upload counter */}
              <div className="text-left">
                <span className={`text-sm font-medium ${totalUploads >= MAX_TOTAL_UPLOADS ? "text-red-400" : "text-white/70"}`}>
                  {totalUploads} / {MAX_TOTAL_UPLOADS} images uploaded
                </span>
              </div>

              {/* CAD Images Upload */}
              <div
                ref={cadUploadMobileRef}
                className="border border-transparent transition-all duration-200 rounded-2xl"
                style={{ position: showGuide && guideStep === 1 ? 'relative' : undefined, zIndex: showGuide && guideStep === 1 ? 201 : undefined, boxShadow: showGuide && guideStep === 1 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)', borderRadius: '1rem' }}
                onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; e.currentTarget.classList.add('border-orange-500/50'); }}
                onDragLeave={(e) => { e.currentTarget.classList.remove('border-orange-500/50'); }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove('border-orange-500/50');
                  const imageUrl = e.dataTransfer.getData('imageUrl') || e.dataTransfer.getData('text/plain');
                  if (imageUrl && totalUploads < MAX_TOTAL_UPLOADS) {
                    setCadImages((prev) => prev.includes(imageUrl) ? prev : [...prev, imageUrl]);
                  }
                }}
              >
                <CADImageUpload
                  cadImages={cadImages}
                  setCadImages={setCadImages}
                  isDragOver={isDragOverCAD}
                  setIsDragOver={setIsDragOverCAD}
                  maxRemaining={maxRemaining}
                />
              </div>

              {/* Mood Images Upload */}
              <div
                ref={moodUploadMobileRef}
                className="border border-transparent transition-all duration-200 rounded-2xl"
                style={{ position: showGuide && guideStep === 2 ? 'relative' : undefined, zIndex: showGuide && guideStep === 2 ? 201 : undefined, boxShadow: showGuide && guideStep === 2 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)', borderRadius: '1rem' }}
                onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; e.currentTarget.classList.add('border-orange-500/50'); }}
                onDragLeave={(e) => { e.currentTarget.classList.remove('border-orange-500/50'); }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove('border-orange-500/50');
                  const imageUrl = e.dataTransfer.getData('imageUrl') || e.dataTransfer.getData('text/plain');
                  if (imageUrl && totalUploads < MAX_TOTAL_UPLOADS) {
                    setMoodImages((prev) => prev.includes(imageUrl) ? prev : [...prev, imageUrl]);
                  }
                }}
              >
                <MoodImageUpload
                  moodImages={moodImages}
                  setMoodImages={setMoodImages}
                  isDragOver={isDragOverMood}
                  setIsDragOver={setIsDragOverMood}
                  maxRemaining={maxRemaining}
                />
              </div>

              {/* Asset Library Panel */}
              <div ref={assetLibraryMobileRef} className="rounded-2xl" style={{ position: showGuide && guideStep === 3 ? 'relative' : undefined, zIndex: showGuide && guideStep === 3 ? 201 : undefined, boxShadow: showGuide && guideStep === 3 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                <AssetLibraryPanel
                  libraryAssets={libraryAssets}
                  setLibraryAssets={setLibraryAssets}
                  generatedImages={sessionLibraryImages}
                  setGeneratedImages={setSessionLibraryImages}
                  currentProjectId={currentProjectId}
                  onImageDeleted={(remaining) => {
                    if (!isGenerating) setCurrentImage(remaining[0] || null);
                  }}
                />
              </div>

              {/* Link to Space Planner */}
              <div ref={spacePlannerMobileRef} className="rounded-xl" style={{ position: showGuide && guideStep === 4 ? 'relative' : undefined, zIndex: showGuide && guideStep === 4 ? 201 : undefined, boxShadow: showGuide && guideStep === 4 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                <button
                  onClick={() => navigate('/cad-space-planner')}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 hover:border-orange-500/40 hover:bg-orange-500/5 transition-all duration-300 group"
                >
                  <Icon name="ArrowRight" size={14} className="text-orange-500 group-hover:translate-x-0.5 transition-transform duration-200" />
                  <span className="text-xs text-white/70 group-hover:text-white/90 transition-colors">Continue editing CAD zones separately</span>
                </button>
              </div>

              {/* Settings */}
              <CADSettings
                selectedRoomType={selectedRoomTypeLeft}
                setSelectedRoomType={setSelectedRoomTypeLeft}
                commonAngleView={commonAngleViewLeft}
                setCommonAngleView={setCommonAngleViewLeft}
                commonAngleRef={commonAngleMobileRef}
                commonAngleGlowStyle={{ position: showGuide && guideStep === 5 ? 'relative' : undefined, zIndex: showGuide && guideStep === 5 ? 201 : undefined, boxShadow: showGuide && guideStep === 5 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}
                angleViewRef={angleViewMobileRef}
                angleViewGlowStyle={{ position: showGuide && guideStep === 6 ? 'relative' : undefined, zIndex: showGuide && guideStep === 6 ? 201 : undefined, boxShadow: showGuide && guideStep === 6 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}
                roomTypeRef={roomTypeMobileRef}
                roomTypeGlowStyle={{ position: showGuide && guideStep === 7 ? 'relative' : undefined, zIndex: showGuide && guideStep === 7 ? 201 : undefined, boxShadow: showGuide && guideStep === 7 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}
                modelSelectionRef={modelSelectionMobileRef}
                modelSelectionGlowStyle={{ position: showGuide && guideStep === 8 ? 'relative' : undefined, zIndex: showGuide && guideStep === 8 ? 201 : undefined, boxShadow: showGuide && guideStep === 8 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}
                aspectRatioGlowRef={aspectRatioMobileRef}
                aspectRatioGlowStyle={{ position: showGuide && guideStep === 9 ? 'relative' : undefined, zIndex: showGuide && guideStep === 9 ? 201 : undefined, boxShadow: showGuide && guideStep === 9 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}
                resolutionGlowRef={resolutionMobileRef}
                resolutionGlowStyle={{ position: showGuide && guideStep === 10 ? 'relative' : undefined, zIndex: showGuide && guideStep === 10 ? 201 : undefined, boxShadow: showGuide && guideStep === 10 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}
                promptGlowRef={promptMobileRef}
                promptGlowStyle={{ position: showGuide && guideStep === 11 ? 'relative' : undefined, zIndex: showGuide && guideStep === 11 ? 201 : undefined, boxShadow: showGuide && guideStep === 11 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}
                selectedAngleView={selectedAngleViewLeft}
                setSelectedAngleView={setSelectedAngleViewLeft}
                selectedModel={selectedModel}
                setSelectedModel={setSelectedModel}
                selectedImageSize={selectedImageSize}
                setSelectedImageSize={setSelectedImageSize}
                isAspectRatioOpen={isAspectRatioOpen}
                setIsAspectRatioOpen={setIsAspectRatioOpen}
                selectedResolution={selectedResolution}
                setSelectedResolution={setSelectedResolution}
                isResolutionOpen={isResolutionOpen}
                setIsResolutionOpen={setIsResolutionOpen}
                prompt={promptLeft}
                setPrompt={setPromptLeft}
                onPromptChange={handlePromptChange}
              />

              {/* Generation Panel */}
              <div ref={generateMobileRef} className="space-y-4 rounded-xl" style={{ position: showGuide && guideStep === 12 ? 'relative' : undefined, zIndex: showGuide && guideStep === 12 ? 201 : undefined, boxShadow: showGuide && guideStep === 12 && guideVisible ? '0 0 32px rgba(180, 120, 60, 0.5), 0 0 80px rgba(180, 120, 60, 0.35), 0 0 150px rgba(180, 120, 60, 0.2)' : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                <GenerationPanel
                  isGenerating={isGenerating}
                  cadImages={cadImages}
                  moodImages={moodImages}
                  generationError={generationError}
                  onGenerate={handleGenerate}
                  onCancelGeneration={handleCancelGeneration}
                />
              </div>
            </div>
          </div>
        </main>
      </div>
      {/* Virtual Tool Guide Overlay */}
      {showGuide && (
        <>
        <div
          className="fixed inset-0 z-[200]"
          onClick={closeGuide}
        >
          <div
            className="absolute inset-0 transition-opacity duration-600 ease-[cubic-bezier(0.4,0,0.2,1)]"
            style={{ background: 'rgba(0, 0, 0, 0.30)', opacity: guideVisible ? 1 : 0 }}
          />
        </div>

        {/* Tooltip bubble */}
        <div
          className="fixed z-[9999] w-[280px] md:w-[300px] pointer-events-none"
          style={(() => {
            if (guideAnimating) {
              return {
                ...guideBubblePosRef.current,
                opacity: 0,
                transform: 'translateY(-50%) translateX(-24px)',
                transition: 'opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1), transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              };
            }
            const desktopRefs = [cadUploadDesktopRef.current, moodUploadDesktopRef.current, assetLibraryDesktopRef.current, spacePlannerDesktopRef.current, commonAngleDesktopRef.current, angleViewDesktopRef.current, roomTypeDesktopRef.current, modelSelectionDesktopRef.current, aspectRatioDesktopRef.current, resolutionDesktopRef.current, promptDesktopRef.current, generateDesktopRef.current, resultDesktopRef.current];
            const mobileRefs = [cadUploadMobileRef.current, moodUploadMobileRef.current, assetLibraryMobileRef.current, spacePlannerMobileRef.current, commonAngleMobileRef.current, angleViewMobileRef.current, roomTypeMobileRef.current, modelSelectionMobileRef.current, aspectRatioMobileRef.current, resolutionMobileRef.current, promptMobileRef.current, generateMobileRef.current, resultMobileRef.current];
            const desktopRef = desktopRefs[guideStep - 1];
            const mobileRef = mobileRefs[guideStep - 1];
            const ref = window.innerWidth >= 768 ? desktopRef : mobileRef;
            if (!ref) return { top: '50%', left: '50%' };
            const rect = ref.getBoundingClientRect();
            const bubbleWidth = window.innerWidth >= 768 ? 300 : 280;
            if (guideStep >= 6 && guideStep <= 13) {
              const pos = { top: rect.top + rect.height / 2, left: rect.left - bubbleWidth - 32 };
              guideBubblePosRef.current = pos;
              return {
                ...pos,
                opacity: guideVisible ? 1 : 0,
                transform: guideVisible ? 'translateY(-50%) translateX(0px)' : 'translateY(-50%) translateX(24px)',
                transition: 'opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1), transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              };
            }
            const pos = { top: rect.top + rect.height / 2, left: rect.right + 32 };
            guideBubblePosRef.current = pos;
            return {
              ...pos,
              opacity: guideVisible ? 1 : 0,
              transform: guideVisible ? 'translateY(-50%) translateX(0px)' : 'translateY(-50%) translateX(-24px)',
              transition: 'opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1), transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            };
          })()}
        >
          <div className="pointer-events-auto">
            {/* Arrow */}
            {guideStep >= 6 && guideStep <= 13 ? (
              <>
                <div
                  className="absolute top-1/2 -right-2 -translate-y-1/2 w-0 h-0"
                  style={{
                    borderTop: '10px solid transparent',
                    borderBottom: '10px solid transparent',
                    borderLeft: '10px solid rgba(254, 131, 56, 0.3)',
                    opacity: guideVisible ? 1 : 0,
                    transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s',
                  }}
                />
                <div
                  className="absolute top-1/2 -right-[6px] -translate-y-1/2 w-0 h-0"
                  style={{
                    borderTop: '9px solid transparent',
                    borderBottom: '9px solid transparent',
                    borderLeft: '9px solid rgba(30, 30, 30, 0.95)',
                    opacity: guideVisible ? 1 : 0,
                    transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s',
                  }}
                />
              </>
            ) : (
            <>
            <div
              className="absolute top-1/2 -left-2 -translate-y-1/2 w-0 h-0"
              style={{
                borderTop: '10px solid transparent',
                borderBottom: '10px solid transparent',
                borderRight: '10px solid rgba(254, 131, 56, 0.3)',
                opacity: guideVisible ? 1 : 0,
                transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s',
              }}
            />
            <div
              className="absolute top-1/2 -left-[6px] -translate-y-1/2 w-0 h-0"
              style={{
                borderTop: '9px solid transparent',
                borderBottom: '9px solid transparent',
                borderRight: '9px solid rgba(30, 30, 30, 0.95)',
                opacity: guideVisible ? 1 : 0,
                transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s',
              }}
            />
            </>
            )}
            <div
              className="rounded-2xl p-5 text-center"
              style={{ background: 'rgb(24, 24, 24)', border: '1px solid rgba(254, 131, 56, 0.3)', boxShadow: '0 8px 32px rgba(0,0,0,0.5), 0 0 20px rgba(254, 131, 56, 0.1)' }}
            >
              <div
                className="flex items-center justify-center gap-2 mb-2"
                style={{
                  opacity: guideVisible ? 1 : 0,
                  transform: guideVisible ? 'translateY(0)' : 'translateY(8px)',
                  transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.1s, transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.1s',
                }}
              >
                <Icon name={[null, "Upload", "LayoutGrid", "FolderOpen", "Layout", "ToggleRight", "Video", "Home", "Cpu", "RectangleHorizontal", "SlidersHorizontal", "MessageSquare", "Sparkles", "Monitor"][guideStep] || "Settings"} size={18} className="text-white/50" />
                <span className="text-sm font-semibold text-white">Step {guideStep}</span>
              </div>
              <p
                className="text-sm text-white/80 leading-relaxed"
                style={{
                  opacity: guideVisible ? 1 : 0,
                  transform: guideVisible ? 'translateY(0)' : 'translateY(8px)',
                  transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.2s, transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.2s',
                }}
              >
                {guideStep === 1 ? (
                  <>Upload your <span className="text-white font-medium italic">2D floor plan</span>. This serves as the spatial blueprint from which the AI generates a fully rendered interior. Without a CAD reference, the image generation will not start.</>
                ) : guideStep === 2 ? (
                  <>Upload reference images that define the desired <span className="text-white font-medium italic">interior style, color palette, materials, and atmosphere</span>. The AI will apply this visual direction onto your 2D floor plan to produce a styled, photorealistic render.</>
                ) : guideStep === 3 ? (
                  <>Generated images which you chose to store inside Roomwork.ai can be <span className="text-white font-medium italic">dragged and dropped</span> into 'Upload CAD image' or 'Mood images'.</>
                ) : guideStep === 4 ? (
                  <>Allows you to <span className="text-white font-medium italic">isolate and redesign individual rooms</span> within a full floor plan without affecting the rest of the layout. Redirects to the Space Planner tool.</>
                ) : guideStep === 5 ? (
                  <>A shortcut toggle between the two most frequently used <span className="text-white font-medium italic">camera perspectives</span>. Select one for quick access without navigating the full Angle view dropdown.</>
                ) : guideStep === 6 ? (
                  <>Selection which allows you to choose the <span className="text-white font-medium italic">camera angle perspective</span> for your room generation.</>
                ) : guideStep === 7 ? (
                  <>Selection which allows you to choose the <span className="text-white font-medium italic">type of room or zone</span> within your floor plan that you want to generate.</>
                ) : guideStep === 8 ? (
                  <>Image generating <span className="text-white font-medium italic">Models</span> which create images. Different models allow you to explore different results.</>
                ) : guideStep === 9 ? (
                  <><span className="text-white font-medium italic">Aspect Ratio</span> — Image ratio: vertical or horizontal (choose the precise ratio according to the dropdown menu icons). Different ratios allow you to explore different room compositions.</>
                ) : guideStep === 10 ? (
                  <>A dropdown selector that allows you to choose the <span className="text-white font-medium italic">output image resolution</span> for your generation. Higher resolution produces a more detailed and sharper image, suitable for professional presentations or print use.</>
                ) : guideStep === 11 ? (
                  <>An optional text field for <span className="text-white font-medium italic">additional instructions</span>: specify materials, furniture styles, lighting conditions, or any design direction not covered by your mood references.</>
                ) : guideStep === 12 ? (
                  <>Hit <span className="text-white font-medium italic">'Generate Image'</span> once you're ready! Choose only the fields that are relevant to your needs.</>
                ) : guideStep === 13 ? (
                  <>Receive the generated image in the <span className="text-white font-medium italic">'Result' window</span>. Use the buttons in the bottom right corner of the Result window to download, delete, or regenerate the image using the exact same settings.</>
                ) : null}
              </p>
              <button
                onClick={() => guideStep < 13 ? nextGuideStep() : closeGuide()}
                className="mt-4 px-5 py-2 glassmorphic-button rounded-xl text-white text-xs font-medium hover:scale-[1.02]"
                style={{
                  opacity: guideVisible ? 1 : 0,
                  transform: guideVisible ? 'translateY(0)' : 'translateY(8px)',
                  transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.3s, transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.3s',
                }}
              >
                {guideStep < 13 ? 'Next' : 'Got it'}
              </button>
            </div>
          </div>
        </div>
        </>
      )}
      <Toaster
        position="bottom-center"
        toastOptions={{
          style: {
            background: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
            backdropFilter: 'blur(10px)'
          }
        }}
      />
    </>
  );
};

export default CADBasedMoodRoomGenerator;
